import { reloadData } from '@axo/form/data-access/backend-sync';
import { DebtRegistry } from '@axo/form/feature/debt-registry/components/DebtRegistry';
import { IDebtsResponse } from '@axo/form/feature/debt-registry/components/hooks/useAssentlyClient';
import { TProviders } from '@axo/shared/auth/providers';
import { DataFetchedFrom } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { DataSharingStatus, MarketCountryCode } from '@axo/shared/types';
import { Stack } from '@axo/ui-core/components/layout/item';
import { Message } from '@axo/ui-core/components/Message';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAxoFormContext } from '../../../../../context/AxoForm/AxoForm';
import ManualDebtRegistry from './ManualDebtRegistry';

export function BankID() {
  const { t } = useTranslation();

  const { baseUrl, dataSharingStatus } = useAxoFormContext();
  const {
    setValue,
    register,
    formState: { errors },
  } = useFormContext();

  const errorRef = useRef<HTMLDivElement>(null);

  const isInternal = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);

    return (
      searchParams.get('traffic') === 'internal' ||
      searchParams.get('utm_campaign') === 'ace'
    );
  }, []);

  const { applicationId, customerJWT } = reloadData.get();

  const providers = useMemo(
    () => ({
      [TProviders.Criipto]: {
        country: MarketCountryCode.Norway,
        label: t('BankID.label'),
      },
      [TProviders.Vipps]: {},
    }),
    [t]
  );

  const onFetchDebt = (debtResponse: IDebtsResponse) => {
    setValue('Debt/Personal/OutstandingAmount', debtResponse.debtTotal, {
      shouldTouch: true,
      shouldDirty: true,
    });
    setValue('Frontend/DebtRegistryDebts', debtResponse.debts);
    setValue('Frontend/SaveAmountNO', debtResponse.savings);
    setValue(
      'LoanApplication/DataFetchedFrom',
      DataFetchedFrom.Gjeldsregisteret,
      {
        shouldTouch: true,
        shouldDirty: true,
      }
    );
  };

  const hasErrors = errors['LoanApplication/DataFetchedFrom'];

  useEffect(() => {
    errorRef.current?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'center',
    });
  }, [hasErrors]);

  if (dataSharingStatus === DataSharingStatus.Disabled) {
    return <ManualDebtRegistry />;
  }

  return (
    <Stack>
      <input
        type="hidden"
        {...register('LoanApplication/DataFetchedFrom', {
          required: !isInternal,
        })}
      />

      <DebtRegistry
        hasCard={false}
        applicationID={applicationId}
        axoApiUrl={baseUrl}
        customerJWT={customerJWT}
        providers={providers}
        onFetchDebt={onFetchDebt}
        isSellingPointsVisible={false}
        isDebtInfoViewFeatureEnabled={false}
      />

      {hasErrors && (
        <div ref={errorRef} data-testid="bank-id-error">
          <Message variant="error" hasIcon>
            {t('BankID.error.required')}
          </Message>
        </div>
      )}

      {isInternal && <ManualDebtRegistry />}
    </Stack>
  );
}
